import { toPng } from 'html-to-image';
import { authReady, getUser, resolveIsAdmin } from '../lib/auth.js';
import { searchFlights } from '../lib/api.js';
import { formatFlightDateShort, formatGroundTime, formatLegDuration, isNextDay, getDaySuffix } from '../lib/flightFormat.js';

const AIRPORTS = [
  { name: 'Aberdeen', code: 'ABZ' }, { name: 'Belfast', code: 'BFS' }, { name: 'Birmingham', code: 'BHX' }, { name: 'Bristol', code: 'BRS' },
  { name: 'Cardiff', code: 'CWL' }, { name: 'Edinburgh', code: 'EDI' }, { name: 'Glasgow International', code: 'GLA' }, { name: 'Leeds Bradford', code: 'LBA' },
  { name: 'Liverpool', code: 'LPL' }, { name: 'London Gatwick', code: 'LGW' }, { name: 'London Heathrow', code: 'LHR' }, { name: 'London Luton', code: 'LTN' },
  { name: 'London Stansted', code: 'STN' }, { name: 'Manchester', code: 'MAN' }, { name: 'Newcastle', code: 'NCL' }, { name: 'Southampton', code: 'SOU' },
];
const TRIP_TYPES = [
  { value: 'day_trip', label: 'Day Trip' }, { value: 'day_trip_weekends', label: 'Day Trip (Weekends)' }, { value: 'overnight', label: 'Overnight' },
  { value: 'overnight_weekends', label: 'Overnight (Weekends)' }, { value: 'long_weekend', label: 'Long Weekend' },
];
const BACKGROUNDS = [
  { id: 'barcelona', label: 'Barcelona', url: '/static/Destination/Barcelona.png' },
  { id: 'berlin', label: 'Berlin', url: '/static/Destination/Berlin.png' },
  { id: 'copenhagen', label: 'Copenhagen', url: '/static/Destination/Copenhagen.png' },
  { id: 'milan', label: 'Milan', url: '/static/Destination/Milan.png' },
  { id: 'oslo', label: 'Oslo', url: '/static/Destination/Oslo.png' },
  { id: 'paris', label: 'Paris', url: '/static/Destination/Paris.png' },
  { id: 'bilbao', label: 'Bilbao', url: '/static/Destination/Bilbao.webp' },
];
const FORMATS = {
  story: { width: 1080, height: 1350, label: 'Instagram 4:5 (1080x1350)', scale: 0.36 },
  landscape: { width: 1200, height: 630, label: '1200x630', scale: 0.52 },
};
const MANUAL_FIELDS = [
  ['cityTo', 'City', 'Copenhagen', 'text'], ['domesticDeparture', 'UK departure airport', 'MAN', 'text'],
  ['foreignAirport', 'Destination airport', 'CPH', 'text'], ['foreignReturnAirport', 'Return departure airport', 'CPH', 'text'],
  ['domesticReturn', 'UK return airport', 'MAN', 'text'], ['price', 'Price', '49', 'number'],
  ['departureDate', 'Departure date', '', 'date'], ['outboundDepartureTime', 'Outbound departure', '06:30', 'time'],
  ['outboundArrivalTime', 'Outbound arrival', '09:45', 'time'], ['returnDate', 'Return date', '', 'date'],
  ['returnDepartureTime', 'Return departure', '18:00', 'time'], ['returnArrivalTime', 'Return arrival', '19:15', 'time'],
];

const root = document.querySelector('[data-admin-studio]');
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const slugify = (city) => String(city || '').toLowerCase().replace(/\s+/g, '-');

let mode = 'search';
let selectedFlight = null;
let selectedBg = BACKGROUNDS[0].url;
let customBg = null;
let format = 'story';
let tripType = 'day_trip';
let manualTripType = 'day_trip';
let manualDraft = defaultManualDraft();

function addDays(isoDate, days) {
  const d = new Date(`${isoDate}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function defaultManualDraft() {
  const dep = new Date();
  dep.setDate(dep.getDate() + 14);
  const depIso = dep.toISOString().slice(0, 10);
  return { cityTo: 'Copenhagen', domesticDeparture: 'MAN', foreignAirport: 'CPH', foreignReturnAirport: 'CPH', domesticReturn: 'MAN', departureDate: depIso, outboundDepartureTime: '06:30', outboundArrivalTime: '09:45', returnDate: depIso, returnDepartureTime: '18:00', returnArrivalTime: '19:15', price: '49' };
}
function parseClock(time) {
  const [h, m] = String(time || '').split(':').map(Number);
  return Number.isNaN(h) || Number.isNaN(m) ? 0 : h * 60 + m;
}
function inferArrivalDate(departureDate, departureTime, arrivalTime) {
  if (!departureDate || !departureTime || !arrivalTime) return departureDate;
  return parseClock(arrivalTime) < parseClock(departureTime) ? addDays(departureDate, 1) : departureDate;
}
function hoursAt(arrivalDate, arrivalTime, returnDate, returnTime) {
  if (!arrivalDate || !arrivalTime || !returnDate || !returnTime) return null;
  const diff = new Date(`${returnDate}T${returnTime}:00`).getTime() - new Date(`${arrivalDate}T${arrivalTime}:00`).getTime();
  return diff > 0 ? Math.round(diff / 3600000) : null;
}
function formatDateRange(depDate, retDate) {
  const dep = new Date(`${depDate}T12:00:00`);
  const ret = new Date(`${retDate}T12:00:00`);
  const depMonth = dep.toLocaleString('en-GB', { month: 'long' });
  const retMonth = ret.toLocaleString('en-GB', { month: 'long' });
  const depDay = `${dep.getDate()}${getDaySuffix(dep.getDate())}`;
  const retDay = `${ret.getDate()}${getDaySuffix(ret.getDate())}`;
  if (depDate === retDate) return `${depDay} ${depMonth}`;
  if (dep.getMonth() === ret.getMonth() && dep.getFullYear() === ret.getFullYear()) return `${depDay} to ${retDay} ${depMonth}`;
  return `${depDay} ${depMonth} to ${retDay} ${retMonth}`;
}
function buildManualFlight() {
  const d = manualDraft;
  const domestic = d.domesticDeparture.trim().toUpperCase();
  const foreign = d.foreignAirport.trim().toUpperCase();
  const city = d.cityTo.trim();
  if (!domestic || !foreign || !city || !d.departureDate) return null;
  const returnDate = d.returnDate || d.departureDate;
  const depArrivalDate = inferArrivalDate(d.departureDate, d.outboundDepartureTime, d.outboundArrivalTime);
  const retArrivalDate = inferArrivalDate(returnDate, d.returnDepartureTime, d.returnArrivalTime);
  return {
    destination_airport: foreign,
    return_origin_airport: d.foreignReturnAirport.trim().toUpperCase() !== foreign ? d.foreignReturnAirport.trim().toUpperCase() : undefined,
    departure_airport: domestic,
    return_arrival_airport: d.domesticReturn.trim().toUpperCase() !== domestic ? d.domesticReturn.trim().toUpperCase() : undefined,
    city_to: city,
    price: Number.parseFloat(d.price) || 0,
    currency: 'GBP',
    departure_date: d.departureDate,
    departure_time: d.outboundDepartureTime,
    departure_arrival_date: depArrivalDate,
    departure_arrival_time: d.outboundArrivalTime,
    return_date: returnDate,
    return_time: d.returnDepartureTime || null,
    return_arrival_time: d.returnArrivalTime || null,
    return_arrival_date: retArrivalDate,
    hours_at_destination: hoursAt(depArrivalDate, d.outboundArrivalTime, returnDate, d.returnDepartureTime),
    DayofWeek: new Date(`${d.departureDate}T12:00:00`).toLocaleString('en-GB', { weekday: 'long' }),
    link: '/flight-search/',
    to_and_from: formatDateRange(d.departureDate, returnDate),
  };
}
function bgForCity(city) {
  const slug = slugify(city);
  return BACKGROUNDS.find((p) => p.id === slug || p.label.toLowerCase() === String(city).toLowerCase())?.url || BACKGROUNDS[0].url;
}
function dayTag(base, compare) {
  return compare && isNextDay(base, compare) ? '<span class="ig-card__day-tag">+1</span>' : '';
}
function legPoint(label, time, code, align = 'start') {
  return `<div class="ig-card__point ig-card__point--${align}"><span class="ig-card__point-label">${label}</span><span class="ig-card__point-time">${esc(time)}</span><span class="ig-card__point-code">${esc(code)}</span></div>`;
}
function cardHtml(flight, tripTypeLabel) {
  const outboundDuration = formatLegDuration(flight.departure_time, flight.departure_arrival_time || '-', flight.departure_date, flight.departure_arrival_date);
  const returnDuration = flight.return_time && flight.return_arrival_time ? formatLegDuration(flight.return_time, flight.return_arrival_time, flight.return_date || undefined, flight.return_arrival_date || flight.return_date || undefined) : null;
  const landUkTime = flight.return_arrival_time || null;
  const landUkCode = flight.return_arrival_airport || flight.departure_airport;
  const returnFrom = flight.return_origin_airport || flight.destination_airport;
  return `<article class="ig-card"><header class="ig-card__header"><div class="ig-card__headline"><h2 class="ig-card__city">${esc(flight.city_to)}</h2><p class="ig-card__meta">${esc(formatFlightDateShort(flight.departure_date))}${tripTypeLabel ? ` · ${esc(tripTypeLabel)}` : ''}</p><p class="ig-card__route">${esc(flight.departure_airport)} ↔ ${esc(flight.destination_airport)}</p></div><div class="ig-card__price-block"><span class="ig-card__price-label">From</span><span class="ig-card__price">£${Math.round(flight.price)}</span><span class="ig-card__price-note">per person</span></div></header>
    <section class="ig-card__journey" aria-label="Flight times"><div class="ig-card__leg"><span class="ig-card__leg-title">Outbound · leave UK</span><div class="ig-card__leg-row">${legPoint('Depart', flight.departure_time, flight.departure_airport)}<div class="ig-card__connector"><span class="ig-card__connector-line" aria-hidden></span><span class="ig-card__connector-duration">${esc(outboundDuration)}</span><span class="ig-card__connector-sub">direct</span></div>${legPoint('Arrive', flight.departure_arrival_time || '-', flight.destination_airport, 'end')}</div><p class="ig-card__leg-foot">Land in ${esc(flight.city_to)} at ${esc(flight.departure_arrival_time || '-')}${dayTag(flight.departure_date, flight.departure_arrival_date)}</p></div>
    ${flight.hours_at_destination != null ? `<div class="ig-card__ground"><span class="ig-card__ground-icon" aria-hidden>◷</span><div><p class="ig-card__ground-title">Time at destination</p><p class="ig-card__ground-value">${esc(formatGroundTime(flight.hours_at_destination, flight.city_to, true))}</p></div></div>` : ''}
    ${flight.return_time ? `<div class="ig-card__leg"><span class="ig-card__leg-title">Return · back to UK</span><div class="ig-card__leg-row">${legPoint('Depart', flight.return_time, returnFrom)}<div class="ig-card__connector"><span class="ig-card__connector-line" aria-hidden></span><span class="ig-card__connector-duration">${esc(returnDuration || '-')}</span><span class="ig-card__connector-sub">direct</span></div>${legPoint('Land UK', landUkTime || '-', landUkCode, 'end')}</div><p class="ig-card__leg-foot">Home by ${esc(landUkTime || '-')} at ${esc(landUkCode)}${dayTag(flight.departure_date, flight.return_arrival_date || flight.return_date)}</p></div>` : '<p class="ig-card__leg-foot ig-card__leg-foot--muted">Return flight times available when you book</p>'}</section>
    ${flight.flights_within_50_diff > 0 ? `<p class="ig-card__alt"><strong>${flight.flights_within_50_diff}</strong> similar flights within £50</p>` : ''}<p class="ig-card__site">gettingaway.co.uk</p></article>`;
}
function canvasHtml(flight, exportFormat, preview = false) {
  const size = FORMATS[exportFormat];
  const label = TRIP_TYPES.find((t) => t.value === (mode === 'manual' ? manualTripType : tripType))?.label;
  return `<div class="flight-export-canvas flight-export-canvas--${exportFormat}" style="width:${size.width}px;height:${size.height}px" ${preview ? '' : 'data-export-root'}><img src="${esc(customBg || selectedBg)}" alt="" class="flight-export-canvas__bg" crossorigin="anonymous" /><div class="flight-export-canvas__shade"></div><div class="flight-export-canvas__card">${cardHtml(flight, label)}</div></div>`;
}
function activeFlight() {
  return mode === 'manual' ? buildManualFlight() : selectedFlight;
}
function renderPreview() {
  const flight = activeFlight();
  const preview = root.querySelector('[data-preview]');
  const target = root.querySelector('[data-export-target]');
  const download = root.querySelector('[data-download]');
  const size = FORMATS[format];
  root.querySelector('[data-export-hint]').textContent = `Export is ${size.label}.`;
  if (!flight) {
    preview.innerHTML = `<div class="admin-studio__preview-empty" style="width:${Math.round(size.width * size.scale)}px;height:${Math.round(size.height * size.scale)}px">${mode === 'manual' ? 'Complete the form (city, airports, dates & times)' : 'Search and select a flight'}</div>`;
    target.innerHTML = '';
    download.disabled = true;
    return;
  }
  target.innerHTML = canvasHtml(flight, format);
  preview.innerHTML = `<div class="admin-studio__preview-viewport" style="width:${Math.round(size.width * size.scale)}px;height:${Math.round(size.height * size.scale)}px"><div class="admin-studio__preview-stage" style="width:${size.width}px;height:${size.height}px;transform:scale(${size.scale})">${canvasHtml(flight, format, true)}</div></div>`;
  download.disabled = false;
}
function populateSelect(select, options, value) {
  select.innerHTML = options.map((o) => `<option value="${esc(o.value || o.code)}">${esc(o.label || `${o.name} (${o.code})`)}</option>`).join('');
  select.value = value;
}
function renderBackgrounds() {
  const grid = root.querySelector('[data-backgrounds]');
  grid.innerHTML = '';
  BACKGROUNDS.forEach((preset) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `admin-studio__bg-thumb${!customBg && selectedBg === preset.url ? ' admin-studio__bg-thumb--active' : ''}`;
    button.title = preset.label;
    button.innerHTML = `<img src="${esc(preset.url)}" alt="${esc(preset.label)}" />`;
    button.addEventListener('click', () => {
      customBg = null;
      selectedBg = preset.url;
      renderBackgrounds();
      renderPreview();
    });
    grid.appendChild(button);
  });
}
function renderManualForm() {
  const form = root.querySelector('[data-manual-form]');
  form.innerHTML = MANUAL_FIELDS.map(([key, label, placeholder, type]) => `<label class="admin-studio__field"><span>${label}</span><input type="${type}" data-manual="${key}" placeholder="${placeholder}" value="${esc(manualDraft[key] || '')}" /></label>`).join('');
  form.querySelectorAll('[data-manual]').forEach((input) => {
    input.addEventListener('input', () => {
      manualDraft[input.dataset.manual] = input.value;
      const flight = buildManualFlight();
      if (flight && !customBg) selectedBg = bgForCity(flight.city_to);
      renderBackgrounds();
      renderPreview();
    });
  });
}
function switchMode(next) {
  mode = next;
  root.querySelectorAll('[data-mode]').forEach((button) => button.classList.toggle('admin-studio__mode-tab--active', button.dataset.mode === mode));
  root.querySelector('[data-search-panel]').hidden = mode !== 'search';
  root.querySelector('[data-manual-panel]').hidden = mode !== 'manual';
  const flight = activeFlight();
  if (flight && !customBg) selectedBg = bgForCity(flight.city_to);
  renderBackgrounds();
  renderPreview();
}

async function initAdminStudio() {
  await authReady;
  const user = getUser();
  if (!user) window.location.replace('/login?next=/account/admin');
  else {
    const isAdmin = await resolveIsAdmin();
    root.querySelector('[data-auth-loading]')?.remove();
    root.querySelector('[data-admin-denied]').hidden = isAdmin;
    root.querySelector('[data-admin-content]').hidden = !isAdmin;
    if (isAdmin) {
      populateSelect(root.querySelector('[data-fly-from]'), AIRPORTS, 'MAN');
      populateSelect(root.querySelector('[data-trip-type]'), TRIP_TYPES, tripType);
      populateSelect(root.querySelector('[data-manual-trip-type]'), TRIP_TYPES, manualTripType);
      renderBackgrounds();
      renderManualForm();
      renderPreview();
      root.querySelector('[data-trip-type]').addEventListener('change', (e) => { tripType = e.target.value; renderPreview(); });
      root.querySelector('[data-manual-trip-type]').addEventListener('change', (e) => { manualTripType = e.target.value; renderPreview(); });
      root.querySelectorAll('[data-mode]').forEach((button) => button.addEventListener('click', () => switchMode(button.dataset.mode)));
      root.querySelector('[data-search-flights]').addEventListener('click', async () => {
        const button = root.querySelector('[data-search-flights]');
        const results = root.querySelector('[data-flight-results]');
        const error = root.querySelector('[data-page-error]');
        button.disabled = true;
        button.textContent = 'Searching...';
        error.hidden = true;
        results.innerHTML = '';
        selectedFlight = null;
        renderPreview();
        try {
          const params = new URLSearchParams({ fly_from: root.querySelector('[data-fly-from]').value, tripType, adults: '1' });
          const flyTo = root.querySelector('[data-fly-to]').value.trim().toUpperCase();
          if (flyTo) params.set('fly_to', flyTo);
          const data = await searchFlights(params);
          const flights = (data.cheapest_flights?.length ? data.cheapest_flights : data.all_flights || []).slice(0, 24);
          selectedFlight = flights[0] || null;
          if (selectedFlight && !customBg) selectedBg = bgForCity(selectedFlight.city_to);
          results.innerHTML = '';
          flights.forEach((flight, index) => {
            const item = document.createElement('button');
            item.type = 'button';
            item.className = `admin-studio__result${index === 0 ? ' admin-studio__result--active' : ''}`;
            item.innerHTML = `<div class="admin-studio__result-city">${esc(flight.city_to)}</div><div class="admin-studio__result-meta">£${Math.round(flight.price)} · ${esc(flight.departure_airport)} -> ${esc(flight.destination_airport)} · ${esc(flight.to_and_from)}</div>`;
            item.addEventListener('click', () => {
              selectedFlight = flight;
              results.querySelectorAll('.admin-studio__result').forEach((el) => el.classList.remove('admin-studio__result--active'));
              item.classList.add('admin-studio__result--active');
              if (!customBg) selectedBg = bgForCity(flight.city_to);
              renderBackgrounds();
              renderPreview();
            });
            results.appendChild(item);
          });
          renderBackgrounds();
          renderPreview();
        } catch (err) {
          error.textContent = err.message || 'Search failed';
          error.hidden = false;
        } finally {
          button.disabled = false;
          button.textContent = 'Search flights';
        }
      });
      root.querySelector('[data-custom-bg]').addEventListener('change', (event) => {
        if (customBg) URL.revokeObjectURL(customBg);
        customBg = event.target.files?.[0] ? URL.createObjectURL(event.target.files[0]) : null;
        renderBackgrounds();
        renderPreview();
      });
      root.querySelectorAll('[data-format]').forEach((button) => button.addEventListener('click', () => {
        format = button.dataset.format;
        root.querySelectorAll('[data-format]').forEach((el) => el.classList.toggle('admin-studio__format-btn--active', el.dataset.format === format));
        renderPreview();
      }));
      root.querySelector('[data-download]').addEventListener('click', async () => {
        const node = root.querySelector('[data-export-root]');
        const flight = activeFlight();
        if (!node || !flight) return;
        const button = root.querySelector('[data-download]');
        button.disabled = true;
        button.textContent = 'Saving...';
        try {
          const dataUrl = await toPng(node, { cacheBust: true, pixelRatio: 1, skipFonts: false });
          const a = document.createElement('a');
          a.href = dataUrl;
          a.download = `gettingaway-${slugify(flight.city_to)}-${format}.png`;
          a.click();
        } finally {
          button.disabled = false;
          button.textContent = 'Save PNG to device';
        }
      });
    }
  }
}

if (root) {
  void initAdminStudio();
}
